import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Layout } from "../components/Layout";
import { getPrinterStats } from "../lib/api";
import { formatSiren } from "../lib/format";
import { useHead } from "../lib/useHead";
import { Lock, Loader2, LogOut, MousePointerClick, Printer } from "lucide-react";

const TOKEN_KEY = "infosociete:stats-token";
const PERIODS = [7, 30, 90];

const Card = ({ children, className = "" }) => (
  <div className={`rounded-lg border border-border bg-[hsl(var(--surface))] ${className}`}>{children}</div>
);

const Kpi = ({ label, value, testId }) => (
  <Card className="p-5">
    <p className="text-sm uppercase tracking-[0.1em] text-[hsl(var(--fg-muted))]">{label}</p>
    <p data-testid={testId} className="mt-2 font-display text-3xl font-semibold text-[hsl(var(--fg-primary))]">{value}</p>
  </Card>
);

const Login = ({ onSubmit, error }) => {
  const [value, setValue] = useState("");
  return (
    <div className="mx-auto max-w-md px-5 py-20">
      <Card className="p-8">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--brand-muted))] text-[hsl(var(--brand))]"><Lock className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-xl font-semibold text-[hsl(var(--fg-primary))]">Tableau de bord privé</h1>
            <p className="text-sm text-[hsl(var(--fg-muted))]">Clics vers printer.fr</p>
          </div>
        </div>
        <form className="mt-6 space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(value.trim()); }}>
          <input
            data-testid="stats-token-input"
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Jeton d'accès"
            className="w-full rounded-md border border-border bg-[hsl(var(--background))] px-3 py-2.5 text-base text-[hsl(var(--fg-primary))] focus:border-[hsl(var(--brand))] focus:outline-none"
          />
          {error && <p data-testid="stats-error" className="text-sm text-[hsl(var(--closed))]">{error}</p>}
          <button data-testid="stats-login-btn" type="submit" className="w-full rounded-md bg-[hsl(var(--brand))] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[hsl(var(--brand-hover))]">
            Accéder
          </button>
        </form>
      </Card>
    </div>
  );
};

const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });

export default function Stats() {
  useHead({ title: "Statistiques — InfoSociété", robots: "noindex,nofollow" });
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [days, setDays] = useState(30);
  const [state, setState] = useState({ loading: false, data: null, error: null });

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    getPrinterStats(token, days)
      .then((data) => { if (alive) { sessionStorage.setItem(TOKEN_KEY, token); setState({ loading: false, data, error: null }); } })
      .catch((e) => {
        if (!alive) return;
        const denied = e?.response?.status === 401;
        if (denied) sessionStorage.removeItem(TOKEN_KEY);
        setState({ loading: false, data: null, error: denied ? "Jeton invalide." : "Impossible de charger les statistiques." });
        if (denied) setToken("");
      });
    return () => { alive = false; };
  }, [token, days]);

  if (!token) return <Layout><Login onSubmit={setToken} error={state.error} /></Layout>;

  const d = state.data;
  const avg = d ? (d.total_periode / d.jours).toFixed(1) : "–";

  return (
    <Layout>
      <div data-testid="stats-dashboard" className="mx-auto max-w-5xl px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-[hsl(var(--fg-muted))]"><Printer className="h-4 w-4" /> Conversion printer.fr</p>
            <h1 className="mt-1 font-display text-3xl font-semibold text-[hsl(var(--fg-primary))]">Clics « Créer mon tampon »</h1>
          </div>
          <div className="flex items-center gap-2">
            {PERIODS.map((p) => (
              <button
                key={p}
                data-testid={`stats-period-${p}`}
                onClick={() => setDays(p)}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${days === p ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand))] text-white" : "border-border bg-[hsl(var(--surface))] text-[hsl(var(--fg-secondary))] hover:border-[hsl(var(--brand))]"}`}
              >
                {p} j
              </button>
            ))}
            <button
              data-testid="stats-logout"
              onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); setState({ loading: false, data: null, error: null }); }}
              title="Se déconnecter"
              className="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--brand))]"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        {state.loading && !d && (
          <div className="flex items-center gap-2 py-16 text-[hsl(var(--fg-muted))]"><Loader2 className="h-5 w-5 animate-spin" /> Chargement…</div>
        )}

        {d && (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <Kpi label={`Clics sur ${d.jours} jours`} value={d.total_periode} testId="kpi-periode" />
              <Kpi label="Moyenne par jour" value={avg} testId="kpi-moyenne" />
              <Kpi label="Total depuis le lancement" value={d.total_global} testId="kpi-global" />
            </div>

            <Card className="mt-6 p-5">
              <h2 className="font-display text-base font-semibold text-[hsl(var(--fg-primary))]">Clics par jour</h2>
              <div className="mt-4 h-72" data-testid="stats-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.par_jour} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 12, fill: "hsl(var(--fg-muted))" }} interval={d.jours > 31 ? 6 : d.jours > 7 ? 2 : 0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "hsl(var(--fg-muted))" }} />
                    <Tooltip
                      cursor={{ fill: "hsl(var(--brand-muted))" }}
                      contentStyle={{ background: "hsl(var(--surface))", border: "1px solid hsl(var(--border))", borderRadius: 6, color: "hsl(var(--fg-primary))" }}
                      labelFormatter={(l) => new Date(l + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                      formatter={(v) => [v, "clics"]}
                    />
                    <Bar dataKey="clics" fill="hsl(var(--brand))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <Card className="p-5">
                <h2 className="font-display text-base font-semibold text-[hsl(var(--fg-primary))]">Entreprises les plus demandées</h2>
                {d.top_entreprises.length === 0 ? (
                  <p className="mt-3 text-sm text-[hsl(var(--fg-muted))]">Aucun clic sur la période.</p>
                ) : (
                  <ul data-testid="stats-top" className="mt-3 divide-y divide-border">
                    {d.top_entreprises.map((t) => (
                      <li key={t.siren} className="flex items-center justify-between py-2.5 text-base">
                        <Link to={`/entreprise/${t.siren}`} className="font-mono text-[hsl(var(--fg-primary))] hover:text-[hsl(var(--brand))]">{formatSiren(t.siren)}</Link>
                        <span className="inline-flex items-center gap-1.5 text-[hsl(var(--fg-secondary))]"><MousePointerClick className="h-4 w-4" /> {t.clics}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card className="p-5">
                <h2 className="font-display text-base font-semibold text-[hsl(var(--fg-primary))]">Origine du clic</h2>
                {d.par_source.length === 0 ? (
                  <p className="mt-3 text-sm text-[hsl(var(--fg-muted))]">Aucun clic sur la période.</p>
                ) : (
                  <ul data-testid="stats-sources" className="mt-3 divide-y divide-border">
                    {d.par_source.map((s) => (
                      <li key={s.source} className="flex items-center justify-between py-2.5 text-base">
                        <span className="text-[hsl(var(--fg-primary))]">{s.source}</span>
                        <span className="text-[hsl(var(--fg-secondary))]">{s.clics}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
